/**
 * Claude model catalog used by the Ask Atlas picker.
 *
 * Keep `id`s in sync with the `ALLOWED_MODELS` set in
 * `supabase/functions/ai-assistant/index.ts`. The Edge Function silently falls
 * back to the default if it receives an id outside the allowlist.
 *
 * Update these identifiers when Anthropic publishes new aliases — see
 * https://docs.anthropic.com/en/docs/about-claude/models for the source of
 * truth on current model names.
 */
export interface ClaudeModel {
  id: string;
  label: string;
  description: string;
}

export const CLAUDE_MODELS: ClaudeModel[] = [
  {
    id: 'claude-opus-4-7',
    label: 'Claude Opus 4.7',
    description: 'Highest reasoning quality. Best for complex analysis.',
  },
  {
    id: 'claude-opus-4-6',
    label: 'Claude Opus 4.6',
    description: 'Previous flagship Opus. Strong reasoning, slightly cheaper.',
  },
  {
    id: 'claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    description: 'Balanced quality and speed. Great default for everyday Q&A.',
  },
  {
    id: 'claude-sonnet-4-5',
    label: 'Claude Sonnet 4.5',
    description: 'Older Sonnet. Good cost/quality tradeoff.',
  },
  {
    id: 'claude-haiku-4-5',
    label: 'Claude Haiku 4.5',
    description: 'Fastest and cheapest. Use for short factual lookups.',
  },
];

export const DEFAULT_CLAUDE_MODEL = 'claude-opus-4-7';

const STORAGE_KEY = 'atlas:assistant:model';

export function getStoredModel(): string {
  if (typeof window === 'undefined') return DEFAULT_CLAUDE_MODEL;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && CLAUDE_MODELS.some((m) => m.id === stored)) return stored;
  return DEFAULT_CLAUDE_MODEL;
}

export function setStoredModel(id: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, id);
}
